if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      connectedCallback() {
        themeBase.onDOMContentLoadedTrigger(() => this.initializeGallery());
      }

      disconnectedCallback() {
        this.elements?.viewer?.removeEventListener('slideChanged', this.onSlideChangedBound);
        this.elements?.thumbnails?.removeEventListener('click', this.onThumbnailClickBound);
        this.initialized = false;
        this.activeMediaGroup = null;
      }

      initializeGallery() {
        if (!this.isConnected || this.initialized) return;
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
        };
        if (!this.elements.viewer) return;
        this.initialized = true;
        this.mql = window.matchMedia('(min-width: 750px)');
        this.onSlideChangedBound ??= debounce(this.onSlideChanged.bind(this), 100);
        this.onThumbnailClickBound ??= event => {
          const thumbnail = event.target.closest('[data-target]');
          if (!thumbnail || thumbnail.hidden || !event.target.closest('button')) return;
          this.setActiveMedia(thumbnail.dataset.target, false);
        };
        this.elements.viewer.addEventListener('slideChanged', this.onSlideChangedBound);
        this.elements.thumbnails?.addEventListener('click', this.onThumbnailClickBound);
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        if (this.pendingMediaGroup) {
          this.applyMediaGroup(this.pendingMediaGroup.ids, this.pendingMediaGroup.reset);
          this.pendingMediaGroup = null;
        } else {
          this.closest('.product')?.querySelector('variant-media-controller')?.syncGallery(this);
        }
      }

      applyMediaGroup(ids, reset = false) {
        if (!this.initialized) {
          this.pendingMediaGroup = { ids, reset };
          return;
        }
        const groupKey = ids.map(String).join(',');
        if (!reset && this.activeMediaGroup === groupKey) return;
        this.activeMediaGroup = groupKey;
        const allowed = new Set(ids.map(String));
        const items = Array.from(this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]'));
        // Products without media keep the theme's existing empty-media presentation.
        if (!items.length) return;
        if (!items.some(item => allowed.has(item.dataset.variantMediaId))) {
          items.forEach(item => allowed.add(item.dataset.variantMediaId));
        }
        this.querySelectorAll('[data-variant-media-id]').forEach(item => {
          const visible = allowed.has(item.dataset.variantMediaId);
          if (!visible && !item.hidden) {
            item.querySelectorAll('video').forEach(video => video.pause());
            item.querySelectorAll('product-model').forEach(model => model.modelViewerUI?.pause());
            item.querySelectorAll('iframe').forEach(frame => {
              if (frame.classList.contains('js-youtube')) frame.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: '' }), '*');
              if (frame.classList.contains('js-vimeo')) frame.contentWindow?.postMessage(JSON.stringify({ method: 'pause' }), '*');
            });
          }
          item.hidden = !visible;
          item.toggleAttribute('data-variant-media-hidden', !visible);
          if (visible) item.removeAttribute('aria-hidden');
          else item.setAttribute('aria-hidden', 'true');
        });
        const visibleItems = items.filter(item => !item.hidden);
        const activeItem = (!reset && visibleItems.find(item => item.classList.contains('is-active'))) || visibleItems[0];
        items.forEach(item => item.classList.toggle('is-active', item === activeItem));
        this.classList.add('has-active');
        const visibleThumbnails = Array.from(this.elements.thumbnails?.querySelectorAll('[data-target]') || []).filter(item => !item.hidden);
        visibleThumbnails.forEach((thumbnail, index) => {
          thumbnail.dataset.mediaPosition = String(index + 1);
          thumbnail.querySelector('button')?.setAttribute('aria-current', String(thumbnail.dataset.target === activeItem.dataset.mediaId));
        });
        const bullets = Array.from(this.elements.viewer.querySelectorAll('button[name="bullet"]')).filter(item => !item.hidden);
        bullets.forEach((bullet, index) => {
          bullet.classList.toggle('active', bullet.dataset.variantMediaId === activeItem.dataset.variantMediaId);
          bullet.setAttribute('aria-label', 'Slide ' + (index + 1));
        });
        [this.elements.viewer, this.elements.thumbnails].filter(Boolean).forEach(slider => {
          const track = slider.slider || slider.querySelector('ul.slider');
          if (reset && track) {
            track.style.scrollBehavior = 'auto';
            setLogicalScrollLeft(track, 0);
            track.style.scrollBehavior = '';
          }
          slider.resetPages?.();
        });
        const counter = this.elements.viewer.querySelector('.slider-counter--total');
        if (counter) counter.textContent = String(visibleItems.length);
        const current = this.elements.viewer.querySelector('.slider-counter--current');
        if (current) current.textContent = String(visibleItems.indexOf(activeItem) + 1);
        const controls = this.elements.viewer.querySelector('.slider-buttons');
        if (controls) controls.hidden = visibleItems.length <= 1;
        this.elements.viewer.querySelectorAll('.slider-button').forEach(button => {
          if (visibleItems.length <= 1) button.disabled = true;
        });
        window.lazyImages?.find(this);
      }

      onSlideChanged(event) {
        if (!event.detail?.currentElement || event.detail.currentElement.hidden) return;
        if (this.hasAttribute('data-native-variant-gallery')) {
          this.elements.viewer.querySelectorAll('.product__media-item').forEach(item => item.classList.toggle('is-active', item === event.detail.currentElement));
        }
        if (!this.elements.thumbnails) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);

        if (!activeMedia || activeMedia.hidden) return;
        this.elements.viewer.style.height = `${this.elements.viewer.offsetHeight}px`;
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');
        this.classList.add('has-active');
        this.elements.viewer.style.height = '';

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail) activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (this.elements.thumbnails) {
            logicalScrollTo(activeMedia.parentElement, {
              left: getLogicalOffsetLeft(activeMedia)
            });
          }
          if (this.closest('quick-add-modal')) return;

          if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
            let offset = 0;

            if (this.dataset.isProductPage) {
              const stickySidebar = activeMedia.closest('sticky-sidebar');
              offset = stickySidebar && themeBase.isDesktop ? (parseFloat(getComputedStyle(stickySidebar).getPropertyValue('--sticky-top').trim()) || 2) * 10 : 20;
            }

            window.scrollTo({
              top: getLogicalRect(activeMedia).top + window.scrollY - offset,
              behavior: 'smooth'
            });
          }
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (!this.elements.thumbnails.slider || this.elements.thumbnails.isSlideVisible?.(thumbnail, 10)) return;

        logicalScrollTo(this.elements.thumbnails.slider, {
          left: getLogicalOffsetLeft(thumbnail)
        });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image || !this.elements.liveRegion) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) {
          scriptController.executeWhenScriptLoaded('deferred-media.js', () => {
            deferredMedia.loadContent(false);
          });
        }
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}